/**
 * Input Sanitization Utilities
 * Sanitizes user input to prevent XSS and other injection attacks
 */

/**
 * Sanitize a string by escaping HTML entities
 * Prevents XSS attacks when displaying user content
 * @param {string} input - Raw user input
 * @returns {string} Sanitized string
 */
export function sanitizeString(input) {
  if (typeof input !== 'string') {
    return input;
  }
  
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

/**
 * Sanitize a string for safe storage
 * Trims whitespace and removes null bytes
 * Does NOT escape HTML (React handles that on render)
 * @param {string} input - Raw user input
 * @returns {string} Cleaned string
 */
export function cleanString(input) {
  if (typeof input !== 'string') {
    return input;
  }
  
  return input
    .trim()
    .replace(/\0/g, ''); // Remove null bytes
}

/**
 * Sanitize task data before storage
 * @param {object} task - Task object with user-provided fields
 * @returns {object} Sanitized task object
 */
export function sanitizeTask(task) {
  if (!task || typeof task !== 'object') {
    return task;
  }
  
  const sanitized = { ...task };
  
  // Clean string fields
  if (sanitized.title) {
    sanitized.title = cleanString(sanitized.title);
  }
  if (sanitized.notes) {
    sanitized.notes = cleanString(sanitized.notes);
  }
  if (sanitized.project) {
    sanitized.project = cleanString(sanitized.project);
  }
  if (sanitized.release) {
    sanitized.release = cleanString(sanitized.release);
  }
  if (sanitized.workflow) {
    sanitized.workflow = cleanString(sanitized.workflow);
  }
  
  return sanitized;
}

/**
 * Sanitize chat message before sending to API
 * @param {string} message - User chat message
 * @returns {string} Sanitized message
 */
export function sanitizeMessage(message) {
  if (typeof message !== 'string') {
    return '';
  }
  
  return cleanString(message);
}

/**
 * Validate and sanitize a date string
 * @param {string} dateString - Date string to validate
 * @returns {string|null} Valid date string or null
 */
export function sanitizeDate(dateString) {
  if (!dateString) {
    return null;
  }
  
  // Try to parse the date
  const date = new Date(dateString);
  
  // Check if valid
  if (isNaN(date.getTime())) {
    return null;
  }
  
  // Return ISO date string (YYYY-MM-DD)
  return date.toISOString().split('T')[0];
}
